import { useCallback, useEffect, useState } from 'react';
import type { CreateEmojiPresetInput, EmojiPresetDTO, UpdateEmojiPresetInput } from '@astral/shared';
import { api } from '../lib/api.js';

export interface UseEmojiPresets {
  presets: EmojiPresetDTO[];
  loading: boolean;
  reload: () => Promise<void>;
  create: (input: CreateEmojiPresetInput) => Promise<EmojiPresetDTO | null>;
  update: (id: string, input: UpdateEmojiPresetInput) => Promise<EmojiPresetDTO | null>;
  remove: (id: string) => Promise<boolean>;
}

/** Load and mutate the current user's emoji presets (server-persisted). */
export function useEmojiPresets(enabled = true): UseEmojiPresets {
  const [presets, setPresets] = useState<EmojiPresetDTO[]>([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      setPresets(await api.listEmojiPresets());
    } catch {
      /* keep the last good list */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (enabled) void reload();
  }, [enabled, reload]);

  const create = useCallback(async (input: CreateEmojiPresetInput): Promise<EmojiPresetDTO | null> => {
    try {
      const created = await api.createEmojiPreset(input);
      setPresets((prev) => [created, ...prev]);
      return created;
    } catch {
      return null;
    }
  }, []);

  const update = useCallback(
    async (id: string, input: UpdateEmojiPresetInput): Promise<EmojiPresetDTO | null> => {
      try {
        const updated = await api.updateEmojiPreset(id, input);
        setPresets((prev) => prev.map((p) => (p.id === id ? updated : p)));
        return updated;
      } catch {
        return null;
      }
    },
    [],
  );

  const remove = useCallback(async (id: string): Promise<boolean> => {
    try {
      await api.deleteEmojiPreset(id);
      setPresets((prev) => prev.filter((p) => p.id !== id));
      return true;
    } catch {
      return false;
    }
  }, []);

  return { presets, loading, reload, create, update, remove };
}
